import { create } from "zustand";
import { CueNote } from "../types";
import { runMigrationsAndMirror } from "../persistence/migrations";
import { getCurrentPacketId, setCurrentPacket, updatePacketNotes } from "../persistence/packets";
import { expandNotesForMatcher } from "../logic/synonym-expander"; // <-- NEW: runtime-only expander

// Re-export type so existing imports like `import { CueNote } from "../state/notes-store"` keep working.
export type { CueNote } from "../types";

/** LocalStorage keys (legacy) used elsewhere in the app */
export const NOTES_KEY = "cueboard.notes";          // legacy
const NOTES_KEY_ALT = "cueboard-notes";             // our short-lived new key
const ONBOARDED_KEY = "cueboard.onboarding";        // legacy
const ONBOARDED_KEY_ALT = "onboarded";              // our short-lived new key

/** ---------- Trigger dedupe + note normalization (surgical) ---------- */
const dedupeTriggers = (triggers?: string[] | null): string[] => {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of Array.isArray(triggers) ? triggers : []) {
    const t = (raw ?? "").trim();
    if (!t) continue;
    const k = t.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(t); // keep first-seen casing
  }
  return out;
};

const normalizeNote = (n: CueNote): CueNote => ({
  ...n,
  id: n.id && n.id.trim() ? n.id : crypto.randomUUID(),
  title: typeof n.title === "string" ? n.title.trim() : "",
  body_markdown: typeof n.body_markdown === "string" ? n.body_markdown : "",
  triggers: dedupeTriggers(n.triggers),
});

const normalizeNotes = (arr: CueNote[]): CueNote[] =>
  (Array.isArray(arr) ? arr : []).map(normalizeNote);

/** Core localStorage helpers to stay compatible with existing routes */
const loadNotes = (): CueNote[] => {
  try {
    const rawLegacy = localStorage.getItem(NOTES_KEY);
    const rawAlt = localStorage.getItem(NOTES_KEY_ALT);
    const raw = rawLegacy ?? rawAlt;
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    const arr = Array.isArray(parsed) ? (parsed as CueNote[]) : [];
    return normalizeNotes(arr);
  } catch {
    return [];
  }
};
const saveNotesLocal = (notes: CueNote[]) => {
  try {
    const norm = normalizeNotes(notes);
    const json = JSON.stringify(norm);
    localStorage.setItem(NOTES_KEY, json);
    localStorage.setItem(NOTES_KEY_ALT, json);
  } catch {}
};

/** Onboarding helpers expected by other files */
export const getOnboarded = (): boolean => {
  try {
    const v = localStorage.getItem(ONBOARDED_KEY);
    if (v != null) return v === "true";
    const alt = localStorage.getItem(ONBOARDED_KEY_ALT);
    return alt === "true";
  } catch {
    return false;
  }
};
export const setOnboarded = (value: boolean): void => {
  try {
    const s = String(value);
    localStorage.setItem(ONBOARDED_KEY, s);
    localStorage.setItem(ONBOARDED_KEY_ALT, s);
  } catch {}
};

/** Store state and actions */
interface NotesState {
  notes: CueNote[];
  currentPacketId: string | null;
  /** Replace in-memory notes + mirror to localStorage + IndexedDB (current packet). */
  setNotes: (notes: CueNote[]) => Promise<void>;
  addNote: (note: CueNote) => Promise<void>;
  updateNote: (id: string, updated: Partial<CueNote>) => Promise<void>;
  deleteNote: (id: string) => Promise<void>;
  /** Set which packet is active and mirror its notes to localStorage. */
  setCurrentPacketId: (id: string | null) => Promise<void>;
  /** Run first-run migration and hydrate current packet into store. */
  hydrate: () => Promise<void>;
}

/** Zustand store */
export const useNotesStore = create<NotesState>((set, get) => ({
  notes: loadNotes(),
  currentPacketId: null,

  setNotes: async (notes: CueNote[]) => {
    // Normalize & dedupe before any mirrors/persistence
    const normalized = normalizeNotes(notes);

    // Mirror first so UI paths relying on localStorage are always consistent
    saveNotesLocal(normalized);

    // Persist to current packet in DB if known
    const pid = get().currentPacketId || (await getCurrentPacketId());
    if (pid) {
      await updatePacketNotes(pid, normalized);
    }

    // Finally reflect in-memory
    set({ notes: normalized });
  },

  addNote: async (note: CueNote) => {
    const updated: CueNote[] = [...get().notes, normalizeNote(note)];
    await get().setNotes(updated);
  },

  updateNote: async (id: string, updated: Partial<CueNote>) => {
    const updatedNotes: CueNote[] = get().notes.map((n: CueNote) =>
      n.id === id ? normalizeNote({ ...n, ...updated }) : n
    );
    await get().setNotes(updatedNotes);
  },

  deleteNote: async (id: string) => {
    const updatedNotes: CueNote[] = get().notes.filter((n: CueNote) => n.id !== id);
    await get().setNotes(updatedNotes);
  },

  setCurrentPacketId: async (id: string | null) => {
    await setCurrentPacket(id);
    // mirror notes from DB to localStorage handled inside setCurrentPacket / updatePacketNotes
    // refresh in-memory notes from localStorage for immediate UI consistency
    const mirrored = loadNotes();
    set({ currentPacketId: id, notes: mirrored });
  },

  hydrate: async () => {
    // 1) ensure packets/mirroring are in a good state
    await runMigrationsAndMirror();
    // 2) adopt current packet id if any
    const pid = await getCurrentPacketId();
    set({ currentPacketId: pid });
    // 3) pull mirrored notes for immediate use
    set({ notes: loadNotes() });
  },
}));

/** Named helper expected by ImportNotes.tsx */
export const setNotes = async (notes: CueNote[]) => {
  await useNotesStore.getState().setNotes(notes);
};

/** NEW: runtime-only helper for matcher construction (no UI bloat, no persistence changes) */
export function getNotesForMatcher(): CueNote[] {
  const base = useNotesStore.getState().notes;
  return expandNotesForMatcher(base);
}
